import { useCallback, useEffect, useMemo, useState } from 'react'
import { createPortal } from 'react-dom'
import { Icon } from '@iconify/react'
import { useAuth } from '@/hooks/useAuth'
import { useCampaignTotpStepUp } from '@/contexts/CampaignTotpStepUpContext'
import { canManageInstitutionCampaigns } from '@/lib/campaign-totp-step-up'
import {
  createCampaignQuestion,
  deleteCampaignQuestion,
  fetchCampaignQuestions,
  updateCampaignQuestion,
} from '@/lib/campaign-questions'
import type { CampaignQuestion, CampaignQuestionInput } from '@/types/campaign-question'
import { formatQuestionTotalResponsesLabel } from '@/lib/campaign-question-stats'
import { QuestionOptionStatsRow } from './QuestionOptionStatsRow'
import { QuestionAnswersTable } from './QuestionAnswersTable'
import styles from './PerguntasManager.module.css'

const MIN_OPTIONS = 2
const MAX_OPTIONS = 3

type QuestionFilter = 'active' | 'inactive'

interface PerguntasManagerProps {
  campaignId: number
}

export function PerguntasManager({ campaignId }: PerguntasManagerProps) {
  const { token, guard, isCollaborator } = useAuth()
  const { runWithStepUp } = useCampaignTotpStepUp()
  const [questions, setQuestions] = useState<CampaignQuestion[]>([])
  const [filterStatus, setFilterStatus] = useState<QuestionFilter>('active')
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [editingQuestion, setEditingQuestion] = useState<CampaignQuestion | null>(null)
  const [isSubmitting, setIsSubmitting] = useState(false)

  const canEdit = canManageInstitutionCampaigns(guard, Boolean(isCollaborator))

  const filteredQuestions = useMemo(() => {
    if (filterStatus === 'active') {
      return questions.filter((question) => question.is_active)
    }
    return questions.filter((question) => !question.is_active)
  }, [questions, filterStatus])

  const loadQuestions = useCallback(async () => {
    if (!token) {
      setQuestions([])
      setIsLoading(false)
      return
    }

    setIsLoading(true)
    setError(null)
    try {
      const result = await fetchCampaignQuestions(campaignId, token)
      setQuestions(result)
    } catch {
      setError('Não foi possível carregar as perguntas.')
    } finally {
      setIsLoading(false)
    }
  }, [campaignId, token])

  useEffect(() => {
    void loadQuestions()
  }, [loadQuestions])

  const handleOpenCreate = () => {
    setEditingQuestion(null)
    setIsModalOpen(true)
  }

  const handleOpenEdit = (question: CampaignQuestion) => {
    setEditingQuestion(question)
    setIsModalOpen(true)
  }

  const handleCloseModal = () => {
    setIsModalOpen(false)
    setEditingQuestion(null)
  }

  const handleSubmit = async (data: CampaignQuestionInput) => {
    if (!token) {
      setError('Você precisa estar autenticado para realizar esta ação.')
      return
    }

    setIsSubmitting(true)
    setError(null)
    try {
      if (editingQuestion) {
        await runWithStepUp(() => updateCampaignQuestion(editingQuestion.id, data, token))
      } else {
        await runWithStepUp(() => createCampaignQuestion(campaignId, data, token))
      }
      await loadQuestions()
      handleCloseModal()
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Não foi possível salvar a pergunta.'
      setError(message)
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleToggleActive = async (question: CampaignQuestion) => {
    if (!token) return

    try {
      await runWithStepUp(() =>
        updateCampaignQuestion(
          question.id,
          { is_active: !question.is_active },
          token,
        )
      )
      await loadQuestions()
    } catch {
      setError('Não foi possível atualizar o status da pergunta.')
    }
  }

  const handleDelete = async (questionId: number) => {
    if (!token) return
    if (!window.confirm('Tem certeza que deseja remover esta pergunta?')) {
      return
    }

    setError(null)
    try {
      await runWithStepUp(() => deleteCampaignQuestion(questionId, token))
      await loadQuestions()
    } catch {
      setError('Não foi possível remover a pergunta.')
    }
  }

  return (
    <section className={styles.section}>
      <div className={styles.header}>
        <div>
          <h3 className={styles.title}>Questionário pós-pagamento</h3>
          <p className={styles.subtitle}>
            Perguntas de múltipla escolha exibidas no widget após a confirmação do PIX, antes da roleta.
          </p>
        </div>
        {canEdit && (
          <button className={styles.addBtn} type="button" onClick={handleOpenCreate}>
            <Icon icon="mdi:plus" aria-hidden />
            Nova pergunta
          </button>
        )}
      </div>

      {error && (
        <div className={styles.errorBanner} role="alert">
          <Icon icon="mdi:alert-circle" aria-hidden />
          {error}
        </div>
      )}

      {questions.length > 0 && (
        <div className={styles.filter} role="group" aria-label="Filtrar perguntas">
          <button
            type="button"
            className={`${styles.filterBtn} ${filterStatus === 'active' ? styles.filterBtnActive : ''}`}
            onClick={() => setFilterStatus('active')}
          >
            Ativas
          </button>
          <button
            type="button"
            className={`${styles.filterBtn} ${filterStatus === 'inactive' ? styles.filterBtnActive : ''}`}
            onClick={() => setFilterStatus('inactive')}
          >
            Inativas
          </button>
        </div>
      )}

      {isLoading ? (
        <div className={styles.loadingState}>Carregando perguntas...</div>
      ) : questions.length === 0 ? (
        <div className={styles.emptyState}>
          <p>Nenhuma pergunta cadastrada.</p>
          {canEdit && (
            <button className={styles.addBtn} type="button" onClick={handleOpenCreate}>
              <Icon icon="mdi:plus" aria-hidden />
              Criar primeira pergunta
            </button>
          )}
        </div>
      ) : filteredQuestions.length === 0 ? (
        <div className={styles.emptyState}>
          <p>
            {filterStatus === 'active'
              ? 'Não há perguntas ativas.'
              : 'Não há perguntas inativas.'}
          </p>
        </div>
      ) : (
        <div className={styles.list}>
          {filteredQuestions.map((question) => (
            <article
              key={question.id}
              className={`${styles.card} ${!question.is_active ? styles.cardInactive : ''}`}
            >
              <div className={styles.cardHeader}>
                <div>
                  <h4 className={styles.cardTitle}>{question.title}</h4>
                  <p className={styles.meta}>
                    Ordem {question.sort_order} · {question.options.length} opções ·{' '}
                    {formatQuestionTotalResponsesLabel(question.total_responses)}
                    {!question.is_active ? ' · Inativa' : ''}
                  </p>
                </div>
              </div>
              <ul className={styles.optionsList}>
                {question.options.map((option) => (
                  <QuestionOptionStatsRow
                    key={option.id}
                    label={option.label}
                    responseCount={option.response_count}
                    totalResponses={question.total_responses}
                  />
                ))}
              </ul>
              {canEdit && token && (
                <QuestionAnswersTable campaignId={campaignId} question={question} token={token} />
              )}
              {canEdit && (
                <div className={styles.actions}>
                  <button
                    type="button"
                    className={styles.actionBtn}
                    onClick={() => handleOpenEdit(question)}
                  >
                    <Icon icon="mdi:pencil-outline" aria-hidden />
                    Editar
                  </button>
                  <button
                    type="button"
                    className={styles.actionBtn}
                    onClick={() => void handleToggleActive(question)}
                  >
                    <Icon
                      icon={question.is_active ? 'mdi:eye-off-outline' : 'mdi:eye-outline'}
                      aria-hidden
                    />
                    {question.is_active ? 'Desativar' : 'Ativar'}
                  </button>
                  <button
                    type="button"
                    className={`${styles.actionBtn} ${styles.actionBtnDanger}`}
                    onClick={() => void handleDelete(question.id)}
                  >
                    <Icon icon="mdi:delete-outline" aria-hidden />
                    Remover
                  </button>
                </div>
              )}
            </article>
          ))}
        </div>
      )}

      {isModalOpen &&
        createPortal(
          <QuestionModal
            question={editingQuestion}
            isSubmitting={isSubmitting}
            onClose={handleCloseModal}
            onSubmit={handleSubmit}
          />,
          document.body,
        )}
    </section>
  )
}

interface QuestionModalProps {
  question: CampaignQuestion | null
  isSubmitting: boolean
  onClose: () => void
  onSubmit: (data: CampaignQuestionInput) => Promise<void>
}

function QuestionModal({ question, isSubmitting, onClose, onSubmit }: QuestionModalProps) {
  const [title, setTitle] = useState(question?.title ?? '')
  const [sortOrder, setSortOrder] = useState(question?.sort_order?.toString() ?? '0')
  const [isActive, setIsActive] = useState(question?.is_active ?? true)
  const [optionLabels, setOptionLabels] = useState<string[]>(() => {
    if (question?.options?.length) {
      return question.options.map((option) => option.label)
    }
    return ['', '']
  })

  const canAddOption = optionLabels.length < MAX_OPTIONS
  const canRemoveOption = optionLabels.length > MIN_OPTIONS

  const handleAddOption = () => {
    if (!canAddOption) return
    setOptionLabels((prev) => [...prev, ''])
  }

  const handleRemoveOption = (index: number) => {
    if (!canRemoveOption) return
    setOptionLabels((prev) => prev.filter((_, itemIndex) => itemIndex !== index))
  }

  const handleOptionChange = (index: number, value: string) => {
    setOptionLabels((prev) => prev.map((label, itemIndex) => (itemIndex === index ? value : label)))
  }

  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault()
    event.stopPropagation()

    const normalizedOptions = optionLabels
      .map((label) => label.trim())
      .filter((label) => label !== '')
      .map((label) => ({ label }))

    if (normalizedOptions.length < MIN_OPTIONS || normalizedOptions.length > MAX_OPTIONS) {
      return
    }

    void onSubmit({
      title: title.trim(),
      is_active: isActive,
      sort_order: Math.max(0, Number.parseInt(sortOrder, 10) || 0),
      options: normalizedOptions,
    })
  }

  const filledOptionsCount = optionLabels.map((label) => label.trim()).filter(Boolean).length
  const isFormValid = title.trim() !== '' && filledOptionsCount >= MIN_OPTIONS

  return (
    <div className={styles.modalOverlay} role="dialog" aria-modal="true" aria-labelledby="question-modal-title">
      <div className={styles.modal}>
        <div className={styles.modalHeader}>
          <h3 id="question-modal-title" className={styles.modalTitle}>
            {question ? 'Editar pergunta' : 'Nova pergunta'}
          </h3>
        </div>

        <form onSubmit={handleSubmit}>
          <div className={styles.modalBody}>
            <div className={styles.field}>
              <label htmlFor="question-title">Pergunta *</label>
              <textarea
                id="question-title"
                className={styles.textarea}
                value={title}
                onChange={(event) => setTitle(event.target.value)}
                placeholder="Ex.: Como você conheceu esta campanha?"
                required
                maxLength={500}
              />
            </div>

            <div className={styles.field}>
              <label htmlFor="question-sort-order">Ordem de exibição</label>
              <input
                id="question-sort-order"
                type="number"
                min={0}
                className={styles.input}
                value={sortOrder}
                onChange={(event) => setSortOrder(event.target.value)}
              />
            </div>

            <div className={styles.field}>
              <span>Opções de resposta ({MIN_OPTIONS} a {MAX_OPTIONS}) *</span>
              <div className={styles.optionsEditor}>
                {optionLabels.map((label, index) => (
                  <div key={`option-${index}`} className={styles.optionRow}>
                    <input
                      type="text"
                      className={styles.input}
                      value={label}
                      onChange={(event) => handleOptionChange(index, event.target.value)}
                      placeholder={`Opção ${index + 1}`}
                      maxLength={255}
                      required={index < MIN_OPTIONS}
                    />
                    {canRemoveOption && (
                      <button
                        type="button"
                        className={styles.removeOptionBtn}
                        onClick={() => handleRemoveOption(index)}
                        aria-label={`Remover opção ${index + 1}`}
                      >
                        <Icon icon="mdi:close" aria-hidden />
                      </button>
                    )}
                  </div>
                ))}
                {canAddOption && (
                  <button type="button" className={styles.addOptionBtn} onClick={handleAddOption}>
                    <Icon icon="mdi:plus" aria-hidden /> Adicionar opção
                  </button>
                )}
              </div>
            </div>

            <label className={styles.checkboxRow}>
              <input
                type="checkbox"
                checked={isActive}
                onChange={(event) => setIsActive(event.target.checked)}
              />
              Pergunta ativa no widget
            </label>
          </div>

          <div className={styles.modalFooter}>
            <button type="button" className={styles.cancelBtn} onClick={onClose}>
              Cancelar
            </button>
            <button type="submit" className={styles.saveBtn} disabled={isSubmitting || !isFormValid}>
              {isSubmitting ? 'Salvando...' : 'Salvar'}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}
